import { useContext, useEffect, useState } from 'react';
import { ModalContext } from 'src/contexts/ModalContext';
import { useGetRetailSaleDetails } from 'src/hooks/api/useRetailSaleDetails';
import { getSalesGroupModalConfigWithDeepLink } from 'src/modals/SalesGroup';

import { GroupMarketplacePaymentDialog } from './GroupMarketplacePaymentDialog';
import { getSaleOutstandingAmt } from './MarketplacePaymentDialog.utils';

/**
 * POS-9097: shared launcher for "Pay" the underlying retail sale from the
 * marketplace-payments surfaces (the payment-line row action and the payment
 * header). Both surfaces only know a `saleId`, not the sale's outstanding
 * balance / currency / buyer / salesGroupId, so those are resolved by lazily
 * fetching the sale on trigger — the query stays disabled (saleId passed as
 * null) until `triggerPay` flips `pendingPay` true.
 *
 * A grouped member sale must be paid at the group level — its per-sale balance
 * is not the group balance, and the sale-level pay path would settle the shared
 * group header across siblings (POS-9097 review). Such a sale routes to the
 * SalesGroup modal; only a TRUE single (groupless) sale pays down itself here
 * (SALE mode).
 */
export function usePayRetailSaleLauncher(
  saleId: number | null | undefined,
  { onPaymentSuccess }: { onPaymentSuccess?: () => void | Promise<void> } = {}
) {
  const { setModal, currentModalConfig } = useContext(ModalContext);
  const [pendingPay, setPendingPay] = useState(false);
  const { data: retailSale, isFetching } = useGetRetailSaleDetails(
    pendingPay ? (saleId ?? null) : null
  );

  useEffect(() => {
    if (!pendingPay || isFetching || !retailSale) {
      return;
    }
    setPendingPay(false);
    if (retailSale.salesGroupId != null) {
      setModal(getSalesGroupModalConfigWithDeepLink(retailSale.salesGroupId));
      return;
    }
    setModal({
      children: (
        <GroupMarketplacePaymentDialog
          saleId={retailSale.id}
          remainingAmount={getSaleOutstandingAmt(retailSale.mkpLines)}
          currencyCode={retailSale.currency ?? 'USD'}
          buyerEmail={retailSale.transDest?.email ?? null}
          cancelTo={currentModalConfig}
          onPaymentSuccess={onPaymentSuccess}
          // Marketplace Payment view pays the full outstanding balance only.
          allowPartialPayment={false}
        />
      ),
    });
  }, [
    pendingPay,
    isFetching,
    retailSale,
    setModal,
    currentModalConfig,
    onPaymentSuccess,
  ]);

  return {
    /** Kick off the lazy sale fetch that resolves and opens the payment flow. */
    triggerPay: () => setPendingPay(true),
    /** True while the sale is being fetched after a trigger. */
    isFetching,
  };
}
